/**
 * Modal popup showing a user's stats: aggregate by game and match history.
 * Click a match row to show that match's game-by-game breakdown.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { GAMES } from '../constants/games';
import { getPlayerProfileData } from '../api/readApi';
import CachedAvatar from './CachedAvatar';
import { useStatsModal } from '../contexts/StatsModalContext';
import { formatDate } from '../utils/dateFormat';
import './ProfileStatsModal.css';

const gamesList = Array.isArray(GAMES) ? GAMES : [];

export default function ProfileStatsModal() {
  const { profileUserId, closeModal } = useStatsModal();
  const { games: enabledGames } = useGame();
  const [profile, setProfile] = useState(null);
  const [statsDoc, setStatsDoc] = useState(null);
  const [matchSummaries, setMatchSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);

  const userId = profileUserId;

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSelectedMatch(null);
    getPlayerProfileData(userId)
      .then((data) => {
        if (cancelled) return;
        setProfile(data?.profile ?? null);
        setStatsDoc(data?.statsDoc ?? null);
        setMatchSummaries(Array.isArray(data?.matchSummaries) ? data.matchSummaries : []);
      })
      .catch((err) => {
        console.error('Error fetching profile for modal:', err);
        if (!cancelled) {
          setProfile(null);
          setStatsDoc(null);
          setMatchSummaries([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId]);

  const getStatsForGame = (gameId) => {
    if (!statsDoc) return null;
    const gs = (statsDoc.gameStats || {})[gameId];
    if (gs) {
      const mw = gs.matchesWon ?? 0;
      const ml = gs.matchesLost ?? 0;
      const total = mw + ml;
      return { matchesWon: mw, matchesLost: ml, totalPoints: gs.totalPoints ?? 0, totalMatches: total, winRate: total > 0 ? ((mw / total) * 100).toFixed(1) : '0' };
    }
    return null;
  };

  const gamesToShow = Array.isArray(enabledGames) ? enabledGames : [];
  const gameRows = gamesToShow.map((game) => ({ game, stats: getStatsForGame(game.id) })).filter((r) => r.stats && r.stats.totalMatches > 0);
  const legacyTotal = statsDoc ? (statsDoc.matchesWon ?? 0) + (statsDoc.matchesLost ?? 0) : 0;
  const allSportsEnabled = gamesToShow.length === gamesList.length;
  const legacyRow = legacyTotal > 0 && gameRows.length === 0 && allSportsEnabled
    ? { game: { id: '_all', name: 'All games', icon: '📊' }, stats: { matchesWon: statsDoc.matchesWon ?? 0, matchesLost: statsDoc.matchesLost ?? 0, totalPoints: statsDoc.totalPoints ?? 0, totalMatches: legacyTotal, winRate: ((statsDoc.matchesWon ?? 0) / legacyTotal * 100).toFixed(1) } }
    : null;
  const rows = legacyRow ? [legacyRow] : gameRows;

  const getGameName = (gameId) => gamesList.find((g) => g.id === gameId)?.name || gameId;

  if (!userId) return null;

  const displayName = profile?.displayName || profile?.email || 'Unknown';
  const avatarUrl = profile?.photoURL || profile?.avatarURL || '';

  return (
    <div className="profile-stats-modal-overlay" onClick={closeModal} role="dialog" aria-modal="true" aria-labelledby="profile-stats-modal-title">
      <div className="profile-stats-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-stats-modal-header">
          <h2 id="profile-stats-modal-title" className="profile-stats-modal-title">Player stats</h2>
          <button type="button" onClick={closeModal} className="profile-stats-modal-close" aria-label="Close">
            ×
          </button>
        </div>
        <div className="profile-stats-modal-body">
          {loading ? (
            <div className="profile-stats-modal-loading">Loading…</div>
          ) : (
            <>
              <div className="profile-stats-modal-identity">
                <CachedAvatar url={avatarUrl} name={displayName} className="profile-stats-modal-avatar" alt={displayName} />
                <span className="profile-stats-modal-name">{displayName}</span>
              </div>
              <h3 className="profile-stats-modal-stats-heading">Stats by game</h3>
              {rows.length === 0 ? (
                <p className="profile-stats-modal-no-stats">No match statistics yet.</p>
              ) : (
                <div className="profile-stats-modal-table-wrap">
                  <table className="profile-stats-modal-table">
                    <thead>
                      <tr>
                        <th>Game</th>
                        <th>Wins</th>
                        <th>Losses</th>
                        <th>Matches</th>
                        <th>Points</th>
                        <th>Win rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(({ game, stats }) => (
                        <tr key={game.id}>
                          <td>{game.icon} {game.name}</td>
                          <td>{stats.matchesWon}</td>
                          <td>{stats.matchesLost}</td>
                          <td>{stats.totalMatches}</td>
                          <td>{stats.totalPoints}</td>
                          <td>{stats.winRate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {matchSummaries.length > 0 && (
                <>
                  <h3 className="profile-stats-modal-stats-heading profile-stats-modal-match-heading">Match history</h3>
                  <p className="profile-stats-modal-match-hint">Click a match to see game-by-game details.</p>
                  <div className="profile-stats-modal-table-wrap profile-stats-modal-matches-wrap">
                    <table className="profile-stats-modal-table profile-stats-modal-matches-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Game</th>
                          <th>Opponent(s)</th>
                          <th>Result</th>
                          <th>Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matchSummaries.map((m) => (
                          <tr
                            key={m.matchId}
                            className={selectedMatch?.matchId === m.matchId ? 'profile-stats-modal-match-row selected' : 'profile-stats-modal-match-row'}
                            onClick={() => setSelectedMatch(selectedMatch?.matchId === m.matchId ? null : m)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedMatch(selectedMatch?.matchId === m.matchId ? null : m);
                              }
                            }}
                          >
                            <td>{m.scheduledAt ? formatDate(m.scheduledAt) : '–'}</td>
                            <td>{getGameName(m.gameId)}</td>
                            <td>{(m.opponentNames || []).join(' / ') || '–'}</td>
                            <td>
                              <span className={m.won ? 'profile-stats-result-won' : 'profile-stats-result-lost'}>
                                {m.won ? 'W' : 'L'}
                              </span>
                            </td>
                            <td>{m.scoreSummary || '–'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {selectedMatch && (
                    <div className="profile-stats-modal-detail" aria-label="Match detail">
                      <h4 className="profile-stats-modal-detail-title">Match details</h4>
                      <dl className="profile-stats-modal-detail-list">
                        <dt>Date</dt>
                        <dd>{selectedMatch.scheduledAt ? formatDate(selectedMatch.scheduledAt) : '–'}</dd>
                        <dt>Game</dt>
                        <dd>{getGameName(selectedMatch.gameId)}</dd>
                        <dt>Opponent(s)</dt>
                        <dd>{(selectedMatch.opponentNames || []).join(' / ') || '–'}</dd>
                        <dt>Result</dt>
                        <dd>
                          <span className={selectedMatch.won ? 'profile-stats-result-won' : 'profile-stats-result-lost'}>
                            {selectedMatch.won ? 'Won' : 'Lost'}
                          </span>
                          {' '}({selectedMatch.gamesWon}–{selectedMatch.gamesLost} games)
                        </dd>
                        <dt>Points</dt>
                        <dd>{selectedMatch.pointsFor} – {selectedMatch.pointsAgainst}</dd>
                        {(selectedMatch.games?.length > 0) && (
                          <>
                            <dt>Games</dt>
                            <dd className="profile-stats-modal-games">
                              {selectedMatch.games.map((g, i) => {
                                const myPts = g.my ?? g.player1 ?? 0;
                                const oppPts = g.opp ?? g.player2 ?? 0;
                                return (
                                  <span key={i} className="profile-stats-modal-game-badge">
                                    Game {i + 1}: {myPts}–{oppPts}
                                  </span>
                                );
                              })}
                            </dd>
                          </>
                        )}
                      </dl>
                      <Link
                        to={`/match/${selectedMatch.matchId}`}
                        className="profile-stats-modal-detail-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View full match →
                      </Link>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
